import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';

export type CloneDateQuickFilter = 'latest' | '7d' | '14d' | '30d';

type UseCloneDateWindowArgs = {
  dateQuickFilter: CloneDateQuickFilter;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
};

export type CloneDateWindow = {
  startDay: string | null;
  endDay: string | null;
  previousStartDay: string | null;
  previousEndDay: string | null;
  sparklineRangeDays: 7 | 14 | 30;
  hasCustomBounds: boolean;
};

export const useCloneDateWindow = ({
  dateQuickFilter,
  fromDate,
  toDate,
}: UseCloneDateWindowArgs): CloneDateWindow =>
  useMemo(() => {
    const today = dayjs().startOf('day');
    const hasCustomBounds = fromDate !== null || toDate !== null;
    const isLatestQuickFilter = dateQuickFilter === 'latest' && !hasCustomBounds;
    const quickFilterDays = dateQuickFilter === '14d' ? 14 : dateQuickFilter === '30d' ? 30 : 7;

    if (isLatestQuickFilter) {
      return {
        startDay: null,
        endDay: null,
        previousStartDay: null,
        previousEndDay: null,
        sparklineRangeDays: 7,
        hasCustomBounds: false,
      };
    }

    const resolvedFrom =
      fromDate?.startOf('day') ??
      toDate?.startOf('day').subtract(quickFilterDays - 1, 'day') ??
      null;
    const resolvedTo = toDate?.startOf('day') ?? today;
    const startDay = hasCustomBounds
      ? (resolvedFrom ?? resolvedTo)
      : today.subtract(quickFilterDays - 1, 'day');
    const endDay = hasCustomBounds ? resolvedTo : today;
    const orderedStartDay = startDay.isAfter(endDay) ? endDay : startDay;
    const orderedEndDay = startDay.isAfter(endDay) ? startDay : endDay;
    const rangeDays = Math.max(orderedEndDay.diff(orderedStartDay, 'day') + 1, 1);
    const previousEndDay = orderedStartDay.subtract(1, 'day');
    const previousStartDay = previousEndDay.subtract(rangeDays - 1, 'day');
    const sparklineRangeDays = rangeDays <= 7 ? 7 : rangeDays <= 14 ? 14 : 30;

    return {
      startDay: orderedStartDay.format('YYYY-MM-DD'),
      endDay: orderedEndDay.format('YYYY-MM-DD'),
      previousStartDay: previousStartDay.format('YYYY-MM-DD'),
      previousEndDay: previousEndDay.format('YYYY-MM-DD'),
      sparklineRangeDays,
      hasCustomBounds,
    };
  }, [dateQuickFilter, fromDate, toDate]);
